import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { get } from "./api";

/** Pessoa com a cor dela no Kompil (cursores, presença). */
export interface HomePerson {
  id: string;
  name: string;
  handle: string;
  avatarUrl: string | null;
  color: string;
}

export interface HomeData {
  stats: {
    weekSeconds: number;
    prevWeekSeconds: number;
    streakDays: number;
    collabSessions: number;
    collaborators: number;
    contributions: number;
    linesChanged: number;
    heatmap: Array<{ day: string; count: number }>;
  };
  resume: {
    projectId: string;
    projectName: string;
    lastVisitAt: string;
    branch: string | null;
    repository: string | null;
    changesSince: Array<HomePerson & { files: number }>;
  } | null;
  live: Array<{
    user: HomePerson;
    projectId: string;
    projectName: string;
    since: string;
    isMe: boolean;
    files: number;
  }>;
  cards: Record<
    string,
    {
      people: Array<HomePerson & { online: boolean; role: string }>;
      lastActivity: { userId: string; name: string; files: number; at: string } | null;
      stack: string | null;
    }
  >;
  onboarding: { terminal: boolean; invited: boolean; published: boolean };
}

export const homeKey = ["me", "home"] as const;

/** Painel inicial: métricas reais, continuar, ao vivo e o checklist (atualiza a cada 30 s). */
export const useHome = (enabled = true) =>
  useQuery({
    queryKey: homeKey,
    queryFn: () => get<HomeData>(`/me/home?tz=${-new Date().getTimezoneOffset()}`),
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

/** "3 h 20 min", "45 min", "0 min". */
export function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

// ───────────── Projetos fixados na barra lateral ─────────────

const PIN_KEY = "kompil.pinnedProjects";

function readPins(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(PIN_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, 12) : [];
  } catch {
    return [];
  }
}

/** Projetos fixados (neste computador), na ordem em que foram fixados. */
export const usePins = create<{ ids: string[]; toggle(id: string): void }>((set, getState) => ({
  ids: readPins(),
  toggle: (id) => {
    const ids = getState().ids.includes(id)
      ? getState().ids.filter((x) => x !== id)
      : [...getState().ids, id];
    set({ ids });
    try {
      localStorage.setItem(PIN_KEY, JSON.stringify(ids));
    } catch {
      // armazenamento bloqueado: vale só nesta sessão
    }
  },
}));

// ───────────── Checklist de primeiros passos ─────────────

const DISMISS_KEY = "kompil.onboardingDismissed";
const TERMINAL_KEY = "kompil.onboarding.terminal";

export const useOnboarding = create<{
  dismissed: boolean;
  terminal: boolean;
  dismiss(): void;
  ranTerminal(): void;
}>((set) => {
  const read = (k: string) => {
    try {
      return localStorage.getItem(k) === "1";
    } catch {
      return false;
    }
  };
  const save = (k: string) => {
    try {
      localStorage.setItem(k, "1");
    } catch {
      // sem armazenamento
    }
  };
  return {
    dismissed: read(DISMISS_KEY),
    terminal: read(TERMINAL_KEY),
    dismiss: () => {
      save(DISMISS_KEY);
      set({ dismissed: true });
    },
    // O terminal da máquina (app) não passa pelo servidor: lembrado aqui.
    ranTerminal: () => {
      save(TERMINAL_KEY);
      set({ terminal: true });
    },
  };
});

// ───────────── Último arquivo aberto em cada projeto (o "Continuar") ─────────────

export const lastFileKey = (projectId: string) => `kompil.lastFile.${projectId}`;

export function lastFileOf(projectId: string): { id: string; path: string } | null {
  try {
    const raw = JSON.parse(localStorage.getItem(lastFileKey(projectId)) ?? "null") as {
      id?: unknown;
      path?: unknown;
    } | null;
    return raw && typeof raw.id === "string" && typeof raw.path === "string"
      ? { id: raw.id, path: raw.path }
      : null;
  } catch {
    return null;
  }
}
